import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import BarcodeDisplay from './BarcodeDisplay';
import GoogleReviews from './GoogleReviews';
import translations from '../translations/customerLookup';
import { celebratePickup } from '../utils/confetti';
import soundEffects from '../utils/sounds';
import { Stella, Checky, CartoonStyles } from './CartoonCharacters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerLookup = () => {
  // Language state - default to 'en' (English, but shows bilingual EN/NL)
  const [language, setLanguage] = useState('en');
  const [isRTL, setIsRTL] = useState(false);
  
  const [searchFirstName, setSearchFirstName] = useState('');
  const [searchLastName, setSearchLastName] = useState('');
  const [searchTracking, setSearchTracking] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showReviewPrompt] = useState(false); // kept for compatibility
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [showNotificationSentModal, setShowNotificationSentModal] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState({}); // Track consent per package (results page)
  const [generalMarketingConsent, setGeneralMarketingConsent] = useState(false); // Track consent on search form
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [ratedPackageName, setRatedPackageName] = useState('');

  // Ref for scrolling to results
  const resultsRef = useRef(null);
  const pollingRef = useRef(null);

  // Get current translation
  const t = translations[language];
  const tDutch = translations['nl'];

  // Poll for package updates (check if picked up) after customer searches
  useEffect(() => {
    if (searched && packages.length > 0) {
      // Start polling every 5 seconds to check if packages are still available
      pollingRef.current = setInterval(async () => {
        try {
          const params = new URLSearchParams();
          if (searchFirstName.trim()) params.append('name', searchFirstName.trim());
          if (searchLastName.trim()) params.append('name', (params.get('name') || '') + ' ' + searchLastName.trim());
          if (searchTracking.trim()) params.append('tracking', searchTracking.trim());
          if (searchPhone.trim()) params.append('phone', searchPhone.trim());
          if (searchEmail.trim()) params.append('email', searchEmail.trim());
          
          const response = await axios.get(`${API}/packages/advanced-search?${params.toString()}`);
          
          // Filter out picked-up packages - only show available ones
          const availablePackages = response.data.filter(pkg => !pkg.is_picked_up);
          
          // Check if any packages were picked up (removed from available list)
          const currentIds = packages.map(p => p.id);
          const newIds = availablePackages.map(p => p.id);
          const removedIds = currentIds.filter(id => !newIds.includes(id));
          
          if (removedIds.length > 0) {
            // Package was picked up - refresh the list
            setPackages(availablePackages);
            if (availablePackages.length === 0) {
              toast.success('✅ Your package has been collected!');
              setSearched(false);
              setPackages([]);
            } else {
              toast.info('Package status updated');
            }
          }
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 5000); // Poll every 5 seconds
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [searched, packages, searchFirstName, searchLastName, searchTracking, searchPhone, searchEmail]);

  // Helper to get bilingual text (English shows EN/NL, others show single language)
  const getBilingualText = (enKey, nlKey = enKey) => {
    if (language === 'en') {
      return `${translations['en'][enKey]} / ${translations['nl'][nlKey]}`;
    }
    return t[enKey];
  };

  // Load language from localStorage on mount
  useEffect(() => {
    const savedLang = localStorage.getItem('customerLookupLanguage');
    if (savedLang && translations[savedLang]) {
      setLanguage(savedLang);
      setIsRTL(translations[savedLang].dir === 'rtl');
    }
  }, []);

  // Handle language change
  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setIsRTL(translations[lang].dir === 'rtl');
    localStorage.setItem('customerLookupLanguage', lang);
  };

  const handleLockToggle = () => {
    if (isLocked) {
      // Show PIN modal to unlock
      setShowPinModal(true);
      setPinInput('');
      setPinError('');
    } else {
      // Lock the page
      setIsLocked(true);
    }
  };

  const handlePinSubmit = async () => {
    try {
      await axios.post(`${API}/auth/verify-pin`, { pin: pinInput });
      setIsLocked(false);
      setShowPinModal(false);
      setPinInput('');
      setPinError('');
    } catch (error) {
      setPinError('Incorrect PIN. Please try again.');
    }
  };

  const handlePinCancel = () => {
    setShowPinModal(false);
    setPinInput('');
    setPinError('');
  };

  const handleSearch = async () => {
    // Validate required fields
    const fullName = `${searchFirstName.trim()} ${searchLastName.trim()}`.trim();
    if (!fullName && !searchTracking.trim()) {
      toast.error('Please enter your name OR tracking number');
      return;
    }

    // Phone or email is MANDATORY for customer security verification
    if (!searchPhone.trim() && !searchEmail.trim()) {
      setShowValidationModal(true);
      return;
    }

    // Validate phone number format (9 or 10 digits)
    if (searchPhone.trim()) {
      const phoneNumbers = searchPhone.replace(/\D/g, ''); // Remove non-digits
      if (phoneNumbers.length < 9 || phoneNumbers.length > 10) {
        toast.error('Phone number must be 9 or 10 digits');
        return;
      }
    }

    setLoading(true);
    try {
      // Build search parameters
      const params = new URLSearchParams();
      if (fullName) {
        params.append('name', fullName);
      }
      if (searchTracking.trim()) {
        params.append('tracking', searchTracking.trim());
      }
      if (searchPhone.trim()) {
        params.append('phone', searchPhone.trim());
      }
      if (searchEmail.trim()) {
        params.append('email', searchEmail.trim());
      }
      
      const response = await axios.get(`${API}/packages/advanced-search?${params.toString()}`);
      
      // Filter out already picked up packages - customers should only see available packages
      const availablePackages = response.data.filter(pkg => !pkg.is_picked_up);
      
      setPackages(availablePackages);
      setSearched(true);
      
      if (availablePackages.length === 0) {
        toast.info('No available packages found with the provided information');
      } else {
        toast.success(`Found ${availablePackages.length} package${availablePackages.length > 1 ? 's' : ''}!`);
        // Customer will click "Ready to Pick Up" button to notify admin
        
        // Auto-scroll to results after a short delay
        setTimeout(() => {
          if (resultsRef.current) {
            resultsRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }
        }, 300);
      }
    } catch (error) {
      console.error('Error searching packages:', error);
      toast.error('Failed to search packages');
    } finally {
      setLoading(false);
    }
  };

  const handlePickup = async (packageId, packageInfo) => {
    try {
      // Collect customer info used in search
      const customerInfo = {
        name: `${searchFirstName.trim()} ${searchLastName.trim()}`.trim() || 'Walk-in Customer',
        phone: searchPhone.trim() || null,
        email: searchEmail.trim() || null
      };
      
      await axios.patch(`${API}/packages/${packageId}`, {
        is_picked_up: true,
        picked_up_by: customerInfo
      });
      
      // Update local state
      setPackages(prev => prev.map(pkg => 
        pkg.id === packageId ? { 
          ...pkg, 
          is_picked_up: true, 
          pickup_date: new Date().toISOString(),
          picked_up_by: customerInfo
        } : pkg
      ));
      
      toast.success(`Package #${packageInfo.box_number} marked as picked up!`);
      
      // Show review prompt after successful pickup
      setTimeout(() => {
        setShowReviewPrompt(true);
      }, 2000);
    } catch (error) {
      console.error('Error marking package as picked up:', error);
      toast.error('Failed to mark package as picked up');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 py-8 px-4" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Back and Lock Buttons */}
      <div className="container mx-auto max-w-4xl mb-6 flex justify-between items-center relative z-50">
        {/* Back Button */}
        {isLocked ? (
          <div className="inline-flex items-center space-x-2 px-6 py-3 bg-gray-400 text-gray-600 font-semibold rounded-xl cursor-not-allowed opacity-60">
            <i className="fas fa-arrow-left"></i>
            <span>{t.backButton}</span>
            <i className="fas fa-lock text-sm ml-2"></i>
          </div>
        ) : (
          <Link
            to="/"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-900 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <i className="fas fa-arrow-left"></i>
            <span>{t.backButton}</span>
          </Link>
        )}

        {/* Lock Button */}
        <button
          onClick={handleLockToggle}
          className={`inline-flex items-center space-x-2 px-6 py-3 font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl ${
            isLocked
              ? 'bg-gradient-to-r from-red-600 to-red-800 text-white hover:from-red-700 hover:to-red-900'
              : 'bg-gradient-to-r from-green-600 to-green-800 text-white hover:from-green-700 hover:to-green-900'
          }`}
        >
          <i className={`fas ${isLocked ? 'fa-lock' : 'fa-lock-open'}`}></i>
          <span>{isLocked ? t.locked : t.lockPage}</span>
        </button>
      </div>

      <div className="max-w-4xl mx-auto space-y-8" data-testid="customer-lookup">
      <div className="text-center mb-12">
        <div className="relative inline-block">
          {/* Decorative background */}
          <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-blue-500 rounded-full blur-3xl opacity-20 transform scale-150 pointer-events-none"></div>
          
          {/* Main icon */}
          <div className="relative w-20 h-20 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl transform rotate-3">
            <i className="fas fa-search text-white text-3xl"></i>
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
              <i className="fas fa-box text-xs text-gray-800"></i>
            </div>
          </div>
          
          <h1 className="text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
              {language === 'en' ? 'Find Your / Vind uw' : t.findYour}
            </span>
            <br />
            <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              {language === 'en' ? 'Package / Pakket' : t.package}
            </span>
          </h1>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {language === 'en' 
              ? 'Secure package pickup service • Professional package management for our local community'
              : t.subtitle
            }
            {language === 'en' && (
              <>
                <br />
                <span className="text-lg text-gray-500">
                  Veilige pakketophaaldienst • Professioneel pakketbeheer voor onze lokale gemeenschap
                </span>
              </>
            )}
          </p>
          
          {/* Business Info */}
          <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl inline-block">
            <p className="text-sm text-gray-700 font-medium">
              <i className="fas fa-store mr-2 text-green-600"></i>
              {language === 'en' ? 'Ashop Local Business Package Service / Ashop Lokale Bedrijfspakketdienst' : t.businessInfo}
            </p>
          </div>
        </div>
      </div>

      {/* Language Selector - Right above search form */}
      <div className="container mx-auto max-w-4xl mb-6">
        <div className="flex items-center justify-center">
          <div className="inline-flex bg-white rounded-2xl shadow-lg p-2 gap-2 flex-wrap justify-center max-w-5xl">
            {Object.keys(translations).map((lang) => (
              <button
                key={lang}
                onClick={() => handleLanguageChange(lang)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
                  language === lang
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md transform scale-105'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
                title={translations[lang].name}
              >
                <span className="text-2xl">{translations[lang].flag}</span>
                <span className="text-sm">{translations[lang].name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        <div className="relative bg-white rounded-3xl p-8 shadow-2xl border border-gray-100 backdrop-blur-sm">
          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-blue-50 rounded-3xl opacity-50"></div>
          
          <div className="relative space-y-6">
            <h3 className="text-center text-lg font-semibold text-gray-700 mb-2">
              {getBilingualText('searchInstructions', 'searchInstructions')}
            </h3>
            
            {/* Primary Search Fields - Vertical Layout */}
            <div className="space-y-4">
              {/* First Name */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <i className="fas fa-user mr-2"></i>
                  {getBilingualText('firstName', 'firstName')}
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                    <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg flex items-center justify-center">
                      <i className="fas fa-user text-white text-sm"></i>
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder={language === 'en' ? 'Enter your first name / Voer uw voornaam in...' : t.firstNamePlaceholder}
                    className="w-full pl-16 pr-6 py-4 text-lg border-2 border-gray-200 rounded-2xl focus:border-green-400 focus:outline-none transition-all duration-300 bg-white shadow-inner"
                    value={searchFirstName}
                    onChange={(e) => setSearchFirstName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    data-testid="customer-firstname-input"
                  />
                </div>
              </div>

              {/* Last Name */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <i className="fas fa-user mr-2"></i>
                  {getBilingualText('lastName', 'lastName')}
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                    <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg flex items-center justify-center">
                      <i className="fas fa-user text-white text-sm"></i>
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder={language === 'en' ? 'Enter your last name / Voer uw achternaam in...' : t.lastNamePlaceholder}
                    className="w-full pl-16 pr-6 py-4 text-lg border-2 border-gray-200 rounded-2xl focus:border-green-400 focus:outline-none transition-all duration-300 bg-white shadow-inner"
                    value={searchLastName}
                    onChange={(e) => setSearchLastName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    data-testid="customer-lastname-input"
                  />
                </div>
              </div>

              {/* OR Divider */}
              <div className="flex items-center justify-center my-4">
                <div className="border-t border-gray-300 flex-grow"></div>
                <div className="mx-4 px-4 py-2 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full">
                  <span className="text-sm font-semibold text-gray-600">{language === 'en' ? 'OR / OF' : t.orDivider}</span>
                </div>
                <div className="border-t border-gray-300 flex-grow"></div>
              </div>

              {/* Tracking Number Search */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <i className="fas fa-barcode mr-2"></i>
                  {getBilingualText('trackingNumber', 'trackingNumber')}
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                      <i className="fas fa-barcode text-white text-sm"></i>
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder={language === 'en' ? 'Enter tracking number / Voer trackingnummer in...' : t.trackingPlaceholder}
                    className="w-full pl-16 pr-6 py-4 text-lg border-2 border-gray-200 rounded-2xl focus:border-blue-400 focus:outline-none transition-all duration-300 bg-white shadow-inner font-mono"
                    value={searchTracking}
                    onChange={(e) => setSearchTracking(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    data-testid="customer-tracking-input"
                  />
                </div>
              </div>
            </div>

            {/* Contact Information Section - Required */}
            <div className="mt-8 p-6 bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl border-2 border-red-200">
              <h4 className="text-lg font-semibold text-gray-800 mb-2 text-center">
                <i className="fas fa-shield-alt mr-2 text-red-600"></i>
                {getBilingualText('requiredVerification', 'requiredVerification')}
              </h4>
              <p className="text-sm text-gray-800 font-medium text-center mb-4">
                {language === 'en' 
                  ? 'You MUST provide either phone number OR email address to search for packages'
                  : t.requiredText
                }
              </p>
              {language === 'en' && (
                <p className="text-xs text-gray-600 text-center mb-4">
                  U MOET een telefoonnummer OF e-mailadres opgeven om pakketten te zoeken
                </p>
              )}
              
              <div className="space-y-4">
                {/* Phone Number */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="fas fa-phone mr-2"></i>
                    {getBilingualText('phoneNumber', 'phoneNumber')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                      <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center">
                        <i className="fas fa-phone text-white text-sm"></i>
                      </div>
                    </div>
                    <input
                      type="tel"
                      placeholder={language === 'en' ? 'Enter 9-10 digit phone / Voer 9-10 cijferig nummer in...' : t.phonePlaceholder}
                      className="w-full pl-16 pr-6 py-4 text-lg border-2 border-gray-200 rounded-2xl focus:border-emerald-400 focus:outline-none transition-all duration-300 bg-white shadow-inner"
                      value={searchPhone}
                      onChange={(e) => setSearchPhone(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      data-testid="customer-phone-input"
                      maxLength="15"
                    />
                  </div>
                </div>

                {/* OR Divider for Contact Fields */}
                <div className="flex items-center justify-center my-3">
                  <div className="border-t border-gray-300 flex-grow"></div>
                  <div className="mx-3 px-3 py-1 bg-white rounded-full border border-gray-300">
                    <span className="text-xs font-medium text-gray-500">{language === 'en' ? 'OR / OF' : t.orDivider}</span>
                  </div>
                  <div className="border-t border-gray-300 flex-grow"></div>
                </div>

                {/* Email Address */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="fas fa-envelope mr-2"></i>
                    {getBilingualText('emailAddress', 'emailAddress')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                      <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-rose-500 rounded-lg flex items-center justify-center">
                        <i className="fas fa-envelope text-white text-sm"></i>
                      </div>
                    </div>
                    <input
                      type="email"
                      placeholder={language === 'en' ? 'Enter your email / Voer uw e-mail in...' : t.emailPlaceholder}
                      className="w-full pl-16 pr-6 py-4 text-lg border-2 border-gray-200 rounded-2xl focus:border-pink-400 focus:outline-none transition-all duration-300 bg-white shadow-inner"
                      value={searchEmail}
                      onChange={(e) => setSearchEmail(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      data-testid="customer-email-input"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Marketing Consent Checkbox - On Search Form */}
            <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-300 rounded-xl shadow-sm">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={generalMarketingConsent}
                  onChange={(e) => setGeneralMarketingConsent(e.target.checked)}
                  className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="text-sm text-gray-700">
                  <span className="font-semibold text-blue-800">📧 Stay informed!</span>
                  <p className="mt-1">
                    I agree to receive updates, promotions, and special offers from Ashop Rotterdam via email or SMS. 
                    You can unsubscribe at any time.
                  </p>
                </div>
              </label>
            </div>
            
            <button
              onClick={handleSearch}
              disabled={loading}
              className="w-full py-5 text-lg font-bold rounded-2xl bg-gradient-to-r from-green-500 to-blue-500 text-white shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-3"
              data-testid="search-packages-btn"
            >
              {loading ? (
                <>
                  <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{language === 'en' ? 'Searching Packages / Pakketten zoeken...' : t.searching}</span>
                </>
              ) : (
                <>
                  <i className="fas fa-search text-xl"></i>
                  <span>{language === 'en' ? 'Find My Package / Vind Mijn Pakket' : t.searchButton}</span>
                  <i className="fas fa-arrow-right"></i>
                </>
              )}
            </button>
            
            {/* Trust badges */}
            <div className="flex justify-center items-center space-x-6 pt-4 text-sm text-gray-500">
              <div className="flex items-center">
                <i className="fas fa-shield-check text-green-500 mr-2"></i>
                <span>{language === 'en' ? 'Secure / Veilig' : t.trustBadges.secure}</span>
              </div>
              <div className="flex items-center">
                <i className="fas fa-bolt text-yellow-500 mr-2"></i>
                <span>{language === 'en' ? 'Instant / Direct' : t.trustBadges.instant}</span>
              </div>
              <div className="flex items-center">
                <i className="fas fa-check-circle text-blue-500 mr-2"></i>
                <span>{language === 'en' ? 'Verified / Geverifieerd' : t.trustBadges.verified}</span>
              </div>
            </div>

            {/* Clear Search Button */}
            <div className="text-center">
              <button
                onClick={() => {
                  setSearchFirstName('');
                  setSearchLastName('');
                  setSearchTracking('');
                  setSearchPhone('');
                  setSearchEmail('');
                  setPackages([]);
                  setSearched(false);
                }}
                className="text-gray-500 hover:text-gray-700 text-sm underline"
              >
                {language === 'en' ? 'Clear All Fields / Alle velden wissen' : t.clearFields}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Star Rating Modal - shown after pickup notification */}
      {showRatingModal && !ratingSubmitted && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl animate-bounce-in">
            <CartoonStyles />
            <div className="text-center">
              <Stella size={100} />
              
              <h3 className="text-2xl font-bold text-gray-800 mb-2 mt-3" data-testid="rating-modal-title">
                How was your experience?
              </h3>
              <p className="text-gray-600 mb-6">
                Your feedback helps us serve you better
              </p>
              
              {/* Star Rating */}
              <div className="flex justify-center space-x-2 mb-6" data-testid="star-rating-container">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    data-testid={`star-${star}`}
                    onClick={() => setSelectedRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="transition-all duration-200 transform hover:scale-125"
                  >
                    <i className={`fas fa-star text-4xl ${
                      star <= (hoverRating || selectedRating)
                        ? 'text-yellow-400'
                        : 'text-gray-300'
                    }`}></i>
                  </button>
                ))}
              </div>
              
              {/* Rating label */}
              {selectedRating > 0 && (
                <p className="text-sm font-semibold mb-4" style={{ animation: 'fadeSlideUp 0.3s ease-out' }}>
                  {selectedRating === 5 && <span className="text-green-600">Excellent!</span>}
                  {selectedRating === 4 && <span className="text-blue-600">Great!</span>}
                  {selectedRating === 3 && <span className="text-yellow-600">Good</span>}
                  {selectedRating === 2 && <span className="text-orange-600">Fair</span>}
                  {selectedRating === 1 && <span className="text-red-600">Needs Improvement</span>}
                </p>
              )}
              
              {/* Optional comment */}
              <textarea
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                placeholder="Leave a comment (optional)..."
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-yellow-400 focus:outline-none text-sm mb-4 resize-none"
                rows="2"
                data-testid="rating-comment-input"
              />
              
              <div className="space-y-3">
                <button
                  data-testid="submit-rating-btn"
                  onClick={async () => {
                    if (selectedRating === 0) {
                      toast.error('Please select a star rating');
                      return;
                    }
                    try {
                      await axios.post(`${API}/ratings`, {
                        rating: selectedRating,
                        customer_name: ratedPackageName || `${searchFirstName} ${searchLastName}`.trim(),
                        comment: ratingComment || null
                      });
                      setRatingSubmitted(true);
                      celebratePickup();
                      soundEffects.playSuccess();
                    } catch (error) {
                      console.error('Error submitting rating:', error);
                      toast.error('Failed to submit rating');
                    }
                  }}
                  disabled={selectedRating === 0}
                  className={`w-full py-4 font-bold rounded-2xl transition-all duration-300 flex items-center justify-center space-x-2 ${
                    selectedRating > 0
                      ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:from-yellow-600 hover:to-orange-600 transform hover:scale-105 shadow-xl'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <i className="fas fa-paper-plane"></i>
                  <span>Submit Rating</span>
                </button>
                
                <button
                  onClick={() => {
                    setShowRatingModal(false);
                    setSelectedRating(0);
                    setRatingComment('');
                  }}
                  className="w-full py-3 text-gray-500 hover:text-gray-700 transition-colors text-sm"
                >
                  Skip
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rating Thank You Modal */}
      {showRatingModal && ratingSubmitted && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl animate-bounce-in">
            <CartoonStyles />
            <div className="text-center">
              <Checky size={100} />
              
              <h3 className="text-2xl font-bold text-gray-800 mb-2 mt-3" data-testid="rating-thank-you">
                Thank You!
              </h3>
              <p className="text-gray-600 mb-4">
                Your {selectedRating}-star rating has been recorded.
              </p>
              
              {/* Show stars */}
              <div className="flex justify-center space-x-1 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className={`fas fa-star text-2xl ${
                    star <= selectedRating ? 'text-yellow-400' : 'text-gray-300'
                  }`}></i>
                ))}
              </div>
              
              <button
                onClick={() => {
                  setShowRatingModal(false);
                  setRatingSubmitted(false);
                  setSelectedRating(0);
                  setRatingComment('');
                }}
                className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold rounded-2xl hover:from-green-600 hover:to-emerald-600 transition-all"
                data-testid="rating-done-btn"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {searched && (
        <div className="space-y-6" ref={resultsRef}>
          {packages.length > 0 ? (
            <>
              <h2 className="text-2xl font-semibold text-gray-800 text-center">
                {packages.length} {t.packagesFound}
              </h2>
              
              <div className="grid gap-6">
                {packages.map((pkg) => (
                  <div 
                    key={pkg.id} 
                    className={`card hover-lift ${
                      pkg.is_picked_up ? 'opacity-60 bg-gray-50' : 'bg-white'
                    }`}
                    data-testid={`package-item-${pkg.box_number}`}
                  >
                    {/* Package Ready Banner */}
                    {!pkg.is_picked_up && (
                      <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-6 rounded-t-2xl mb-6 -mx-6 -mt-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="w-16 h-16 bg-white bg-opacity-30 rounded-full flex items-center justify-center animate-pulse">
                              <i className="fas fa-check-circle text-3xl"></i>
                            </div>
                            <div>
                              <h3 className="text-3xl font-bold">📦 {t.packageReady}</h3>
                              <p className="text-white text-opacity-90 text-lg">{t.packageWaiting}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Box Number - Large and Prominent */}
                    <div className="flex items-center justify-center mb-6">
                      <div className="text-center">
                        <p className="text-sm text-gray-600 uppercase font-semibold mb-2">{t.boxNumberTitle}</p>
                        <div className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-orange-500 to-red-600 rounded-3xl shadow-2xl transform hover:scale-110 transition-all">
                          <span className="text-6xl font-black text-white drop-shadow-lg">#{pkg.box_number || '?'}</span>
                        </div>
                        <p className="text-lg text-gray-700 font-semibold mt-3">
                          {t.lookForBox}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold ${
                          pkg.is_picked_up ? 'bg-gray-200 text-gray-600' : 'bg-blue-100 text-blue-600'
                        }`}>
                          <i className="fas fa-user"></i>
                        </div>
                        
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-gray-800 mb-2">
                            {pkg.recipient_name}
                          </h3>
                          
                          {/* Tracking Numbers Display */}
                          {pkg.tracking_numbers && pkg.tracking_numbers.length > 0 && (
                            <div className="mb-4 p-5 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl border-2 border-indigo-200">
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center">
                                  <i className="fas fa-barcode text-indigo-600 mr-3 text-lg"></i>
                                  <div>
                                    <p className="text-xs text-indigo-600 font-medium uppercase tracking-wide">Tracking Numbers</p>
                                    {pkg.tracking_numbers.map((tracking, idx) => (
                                      <p key={idx} className="font-mono font-bold text-indigo-800 text-lg">
                                        {tracking}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <div className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-semibold">
                                    VERIFIED
                                  </div>
                                  <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                                    SCANNABLE
                                  </div>
                                </div>
                              </div>
                              
                              {/* Large Barcode for Customer - Mobile Optimized */}
                              <div className="bg-white p-3 sm:p-6 rounded-xl border border-gray-200 shadow-inner">
                                <div className="text-center mb-2">
                                  <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                                    <i className="fas fa-qrcode mr-2"></i>
                                    Scannable Barcode
                                  </h4>
                                </div>
                                
                                <div className="flex justify-center overflow-hidden px-2">
                                  <BarcodeDisplay 
                                    trackingNumber={pkg.tracking_numbers[0]}
                                    carrier={pkg.carrier}
                                    size="large"
                                    showNumber={true}
                                  />
                                </div>
                                
                                <div className="mt-3 text-center space-y-1">
                                  <p className="text-xs sm:text-sm font-medium text-gray-700">
                                    <i className="fas fa-truck mr-2 text-indigo-600"></i>
                                    {pkg.carrier} Compatible Barcode
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Show this barcode to staff for quick scanning
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Legacy tracking from notes */}
                          {(!pkg.tracking_numbers || pkg.tracking_numbers.length === 0) && pkg.notes && pkg.notes.includes('Tracking:') && (
                            <div className="mb-4 p-5 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl border-2 border-indigo-200">
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center">
                                  <i className="fas fa-barcode text-indigo-600 mr-3 text-lg"></i>
                                  <div>
                                    <p className="text-xs text-indigo-600 font-medium uppercase tracking-wide">Tracking Number</p>
                                    <p className="font-mono font-bold text-indigo-800 text-lg">
                                      {pkg.notes.split('Tracking: ')[1].split(' ')[0]}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <div className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-semibold">
                                    VERIFIED
                                  </div>
                                  <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                                    SCANNABLE
                                  </div>
                                </div>
                              </div>
                              
                              {/* Large Barcode for Customer - Mobile Optimized */}
                              <div className="bg-white p-3 sm:p-6 rounded-xl border border-gray-200 shadow-inner">
                                <div className="text-center mb-2">
                                  <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                                    <i className="fas fa-qrcode mr-2"></i>
                                    Scannable Barcode
                                  </h4>
                                </div>
                                
                                <div className="flex justify-center overflow-hidden px-2">
                                  <BarcodeDisplay 
                                    trackingNumber={pkg.notes.split('Tracking: ')[1].split(' ')[0]}
                                    carrier={pkg.carrier}
                                    size="large"
                                    showNumber={true}
                                  />
                                </div>
                                
                                <div className="mt-3 text-center space-y-1">
                                  <p className="text-xs sm:text-sm font-medium text-gray-700">
                                    <i className="fas fa-truck mr-2 text-indigo-600"></i>
                                    {pkg.carrier} Compatible Barcode
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Show this barcode to staff for quick scanning
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          <div className="space-y-2">
                            <div className="flex items-center text-gray-600">
                              <i className="fas fa-truck mr-3 text-lg"></i>
                              <span className="font-semibold">{pkg.carrier || 'No carrier specified'}</span>
                            </div>
                            <div className="flex items-center text-gray-500">
                              <i className="fas fa-calendar mr-3 text-lg"></i>
                              <span>Received: {formatDate(pkg.date_added)}</span>
                            </div>
                            {pkg.is_picked_up && pkg.pickup_date && (
                              <div className="flex items-center text-green-600">
                                <i className="fas fa-check-circle mr-3 text-lg"></i>
                                <span className="font-medium">Picked up: {formatDate(pkg.pickup_date)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Marketing Consent Checkbox - Optional, doesn't affect pickup */}
                      {!pkg.is_picked_up && (
                        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <label className="flex items-start space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={marketingConsent[pkg.id] || false}
                              onChange={(e) => setMarketingConsent({...marketingConsent, [pkg.id]: e.target.checked})}
                              className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <div className="text-sm text-gray-700">
                              <span className="font-medium">Stay informed!</span>
                              <p className="mt-1">
                                I agree to receive updates, promotions, and special offers from Ashop Rotterdam via email or SMS. 
                                You can unsubscribe at any time.
                              </p>
                            </div>
                          </label>
                        </div>
                      )}
                      
                      <div className="text-right">
                        {pkg.is_picked_up ? (
                          <div className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-600 rounded-lg">
                            <i className="fas fa-check-circle mr-2"></i>
                            {t.alreadyPickedUp}
                          </div>
                        ) : (
                          <button
                            onClick={async () => {
                              try {
                                // Prepare search details as JSON string
                                const searchDetails = {
                                  first_name: searchFirstName.trim() || null,
                                  last_name: searchLastName.trim() || null,
                                  tracking: searchTracking.trim() || null,
                                  phone: searchPhone.trim() || null,
                                  email: searchEmail.trim() || null
                                };
                                
                                // If user consented to marketing, save their info separately (non-blocking)
                                if (generalMarketingConsent || marketingConsent[pkg.id]) {
                                  try {
                                    await axios.post(`${API}/marketing-consent`, {
                                      first_name: searchFirstName.trim() || pkg.recipient_name.split(' ')[0] || '',
                                      last_name: searchLastName.trim() || pkg.recipient_name.split(' ').slice(1).join(' ') || '',
                                      email: searchEmail.trim() || null,
                                      phone: searchPhone.trim() || null,
                                      source: 'package_pickup'
                                    });
                                  } catch (consentErr) {
                                    console.error('Failed to save consent, but continuing with pickup:', consentErr);
                                    // Don't block pickup if consent save fails
                                  }
                                }
                                
                                // Notify admin that customer is ready to pick up (existing flow - unchanged)
                                await axios.post(`${API}/notify-customer-search`, null, {
                                  params: {
                                    package_id: pkg.id,
                                    customer_name: pkg.recipient_name,
                                    tracking_number: pkg.tracking_numbers && pkg.tracking_numbers.length > 0 
                                      ? pkg.tracking_numbers[0] 
                                      : 'No tracking',
                                    box_number: pkg.box_number || 0,
                                    carrier: pkg.carrier || 'Not specified',  // Send carrier information
                                    search_details: JSON.stringify(searchDetails)
                                  }
                                });
                                
                                // Show big popup confirmation
                                setShowNotificationSentModal(true);
                                setRatedPackageName(pkg.recipient_name);
                                
                                // Show rating modal after a delay
                                setTimeout(() => {
                                  setShowNotificationSentModal(false);
                                  setTimeout(() => {
                                    setShowRatingModal(true);
                                  }, 500);
                                }, 3000);
                              } catch (err) {
                                console.error('Notification error:', err);
                                toast.error('Failed to notify admin. Please try again.');
                              }
                            }}
                            className="btn-success px-8 py-4 rounded-xl font-bold text-lg flex items-center space-x-3 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                            data-testid={`pickup-btn-${pkg.box_number}`}
                          >
                            <i className="fas fa-bell text-xl"></i>
                            <span>Ready to Pick Up</span>
                            <i className="fas fa-arrow-right text-xl"></i>
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {pkg.notes && (
                      <div className="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                        <p className="text-sm text-yellow-800">
                          <i className="fas fa-sticky-note mr-2"></i>
                          <strong>Note:</strong> {pkg.notes}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="card text-center py-12">
              <i className="fas fa-search text-gray-300 text-6xl mb-4"></i>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                {t.noPackagesFound}
              </h3>
              <p className="text-gray-500">
                {t.checkSpelling}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Validation Modal - Contact Information Required */}
      {showValidationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl transform scale-105 relative">
            {/* Close Button (X) in top-right */}
            <button
              onClick={() => setShowValidationModal(false)}
              className="absolute top-4 right-4 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200"
            >
              <i className="fas fa-times text-gray-600"></i>
            </button>
            
            <div className="text-center">
              {/* Warning Icon */}
              <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
                <i className="fas fa-exclamation-triangle text-white text-3xl"></i>
              </div>
              
              {/* Title */}
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                {t.requiredVerification}
              </h3>
              
              {/* Main Message */}
              <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-6 mb-6 border-2 border-red-200">
                <p className="text-lg font-semibold text-red-800 mb-3">
                  <i className="fas fa-shield-alt mr-2"></i>
                  {t.securityNotice}
                </p>
                <p className="text-gray-700 text-base leading-relaxed">
                  {t.securityMessage}
                </p>
                <p className="text-gray-600 text-sm mt-3">
                  {t.securityHelp}
                </p>
              </div>
              
              {/* Understand Button */}
              <div className="text-center">
                <button
                  onClick={() => setShowValidationModal(false)}
                  className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-purple-600 transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2 mx-auto"
                >
                  <i className="fas fa-check"></i>
                  <span>{t.understand}</span>
                </button>
              </div>
              
              {/* Info Footer */}
              <div className="mt-6 text-center">
                <p className="text-xs text-gray-400 flex items-center justify-center">
                  <i className="fas fa-lock mr-2"></i>
                  {t.informationSecure}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Google Reviews Section */}
      <GoogleReviews />

      {/* PIN Modal */}
      {showPinModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-lock text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">{t.enterPinToUnlock}</h3>
              <p className="text-gray-600">{t.enterPinPrompt}</p>
            </div>

            <div className="space-y-4">
              <input
                type="password"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePinSubmit()}
                placeholder={t.enterPin}
                maxLength="4"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none text-center text-2xl tracking-widest"
                autoFocus
              />
              
              {pinError && (
                <div className="text-red-600 text-sm text-center font-semibold">
                  <i className="fas fa-exclamation-circle mr-2"></i>
                  {t.incorrectPin}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={handlePinCancel}
                  className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-400 transition-all duration-300"
                >
                  {t.cancel}
                </button>
                <button
                  onClick={handlePinSubmit}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-900 transition-all duration-300"
                >
                  {t.unlock}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Notified Popup Modal */}
      {showNotificationSentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl p-12 max-w-2xl w-full shadow-2xl transform scale-105 animate-slideUp">
            <div className="text-center">
              {/* Success Icon */}
              <div className="w-32 h-32 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl animate-pulse">
                <i className="fas fa-check-circle text-white text-6xl"></i>
              </div>
              
              <h3 className="text-4xl font-bold text-gray-800 mb-4">
                ✅ Admin Has Been Notified!
              </h3>
              
              <p className="text-2xl text-gray-700 mb-8 leading-relaxed">
                Please wait at the counter.<br/>
                Our staff will bring your package shortly.
              </p>
              
              <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl p-6 mb-8">
                <p className="text-lg text-gray-600">
                  <i className="fas fa-info-circle mr-2 text-blue-600"></i>
                  The staff member will verify your identity before handing over your package.
                </p>
              </div>
              
              <button
                onClick={() => setShowNotificationSentModal(false)}
                className="px-12 py-5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xl font-bold rounded-2xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105"
              >
                Got It! 👍
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default CustomerLookup;